/**
 * Parse a timestamp that came from our backend into a correct `Date`.
 *
 * The backend stores/serializes most timestamps as **naive UTC** — e.g.
 * `"2026-07-18T13:00:00"` or `"2026-07-18T13:00:00.123456"` with NO timezone
 * suffix (SQLModel columns default to `utcnow_naive()` and Pydantic emits
 * `.isoformat()`). JavaScript's `new Date("2026-07-18T13:00:00")` parses a
 * date-TIME string with no timezone as **local time**, so for a viewer in
 * EDT (UTC-4) a UTC timestamp is read ~4 hours in the future — which makes
 * relative-time labels say "in about 4 hours" instead of "about 4 hours ago".
 *
 * This helper treats a suffix-less string as UTC (the backend's actual
 * intent) while trusting any string that already carries a timezone. It is
 * idempotent and safe to route every server timestamp through.
 */
export function parseServerDate(
  value: string | number | Date | null | undefined
): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // Already has an explicit timezone (Z, +00:00, -0400, …) — trust it.
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  // Date-only ("2026-07-18") is already parsed as UTC by JS — leave it.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);

  let iso = raw;
  if (!hasTimezone && !isDateOnly) {
    // Normalize a possible space separator ("2026-07-18 13:00:00") to ISO
    // "T", then mark it UTC so it isn't reinterpreted as local time.
    iso = `${raw.replace(' ', 'T')}Z`;
  }

  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
